import {
	DndContext,
	DragOverlay,
	MeasuringStrategy,
	PointerSensor,
	TouchSensor,
	useDroppable,
	useSensor,
	useSensors,
	type DragEndEvent,
	type DragMoveEvent,
	type DragOverEvent,
	type DragStartEvent,
} from "@dnd-kit/core";
import {
	defaultAnimateLayoutChanges,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS as dndCss, getEventCoordinates } from "@dnd-kit/utilities";
import {
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
	type MouseEvent,
	type ReactNode,
} from "react";

import type { HubTagColorId } from "./hub-task-colors.js";
import { taskBoardCollision, taskBoardCompactDrag, taskBoardPointer } from "./task-board-collision.js";
import { snapMobileDragOverlayToPointer } from "./task-board-drag-modifiers.js";
import {
	TASK_DROP_ANIMATION,
	TASK_DROP_ANIMATION_MS,
	TASK_SORTABLE_TRANSITION,
} from "./task-board-dnd.js";
import {
	TaskBoardCard,
	TaskColumnEmptyCard,
	TaskDragPill,
	TaskDropPlaceholder,
	type TaskDragSlotSize,
} from "./task-board-card.js";
import {
	TASK_BOARD_STATUSES,
	applyColumnMove,
	boardMoveFromOrder,
	columnAtPointer,
	statusTabAtPointerFromBar,
	columnId,
	previewIndexHeld,
	columnIdsFromTasks,
	columnOrderKey,
	columnTopId,
	findTaskColumn,
	resolveDropTarget,
	resolveFinalDropTarget,
	syncColumnScrollForTopDrop,
	type HubTask,
	type TaskBoardStatus,
	type TaskColumnIds,
} from "./task-board-order.js";

function SortableTaskCard({
	task,
	onOpen,
	onMenu,
	cardHint,
	status,
	tagList,
	checklistDone,
	checklistTotal,
	attachmentCount,
	coverImageUrl,
	overdue,
	createdLabel,
	assigneeLabel,
	assigneeKey,
	tagColorByLowerName,
	assigneeColorByLowerName,
	dueLabel,
	dueLabelCompact,
	dragSlotSize,
	compact,
	boardDraggable,
	columnInteractionsEnabled,
	enableMobileSwipe,
	onSwipeStatus,
}: {
	task: HubTask;
	onOpen: () => void;
	onMenu: (event: MouseEvent) => void;
	cardHint: string;
	status: TaskBoardStatus;
	tagList: string[];
	checklistDone: number;
	checklistTotal: number;
	attachmentCount: number;
	coverImageUrl: string;
	overdue: boolean;
	createdLabel: string;
	assigneeLabel: string;
	assigneeKey: string;
	tagColorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>;
	assigneeColorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>;
	dueLabel: string;
	dueLabelCompact: string;
	dragSlotSize?: TaskDragSlotSize;
	compact: boolean;
	boardDraggable: boolean;
	columnInteractionsEnabled: boolean;
	enableMobileSwipe?: boolean;
	onSwipeStatus?: (direction: -1 | 1) => void;
}) {
	const id = task.id || task.title;
	const sortableDisabled = !task.id || !boardDraggable || !columnInteractionsEnabled;
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id,
		disabled: sortableDisabled,
		data: { column: status },
		animateLayoutChanges: defaultAnimateLayoutChanges,
		transition: TASK_SORTABLE_TRANSITION,
	});
	const sortableStyle: CSSProperties = {
		transform: dndCss.Transform.toString(transform),
		transition,
		...(sortableDisabled || isDragging
			? {}
			: compact
				? { touchAction: "pan-y" as const }
				: { touchAction: "none" as const }),
	};
	if (isDragging && dragSlotSize) {
		sortableStyle.height = dragSlotSize.height;
		sortableStyle.minHeight = dragSlotSize.height;
		sortableStyle.width = "100%";
	}

	return (
		<div
			ref={setNodeRef}
			style={sortableStyle}
			className={isDragging ? "task-sortable-slot is-dragging" : "task-sortable-slot"}
			{...attributes}
			{...(sortableDisabled ? {} : listeners)}
		>
			{isDragging ? (
				<TaskDropPlaceholder size={dragSlotSize} />
			) : (
				<TaskBoardCard
					task={task}
					status={status}
					tagList={tagList}
					checklistDone={checklistDone}
					checklistTotal={checklistTotal}
					attachmentCount={attachmentCount}
					coverImageUrl={coverImageUrl}
					overdue={overdue}
					createdLabel={createdLabel}
					assigneeLabel={assigneeLabel}
					assigneeKey={assigneeKey}
					tagColorByLowerName={tagColorByLowerName}
					assigneeColorByLowerName={assigneeColorByLowerName}
					dueLabel={dueLabel}
					dueLabelCompact={dueLabelCompact}
					cardHint={cardHint}
					isDraggable={!sortableDisabled}
					enableMobileSwipe={enableMobileSwipe}
					onSwipeStatus={onSwipeStatus}
					onOpen={onOpen}
					onMenu={onMenu}
				/>
			)}
		</div>
	);
}

function ColumnTopDrop({ status, enabled }: { status: TaskBoardStatus; enabled: boolean }) {
	const { setNodeRef, isOver } = useDroppable({
		id: columnTopId(status),
		disabled: !enabled,
	});
	if (!enabled) return null;
	return (
		<div
			ref={setNodeRef}
			className={isOver ? "task-col-top-drop is-over" : "task-col-top-drop"}
			aria-hidden="true"
		/>
	);
}

function EmptyColumnDrop({
	status,
	dragSlotSize,
	enabled,
}: {
	status: TaskBoardStatus;
	dragSlotSize?: TaskDragSlotSize;
	enabled: boolean;
}) {
	const { setNodeRef, isOver } = useDroppable({ id: columnId(status), disabled: !enabled });
	return (
		<div ref={setNodeRef} className={isOver ? "task-col-drop is-over" : "task-col-drop"}>
			{isOver ? <TaskDropPlaceholder size={dragSlotSize} /> : <TaskColumnEmptyCard status={status} />}
		</div>
	);
}

function ColumnList({
	status,
	droppable,
	children,
}: {
	status: TaskBoardStatus;
	droppable: boolean;
	children: ReactNode;
}) {
	const { setNodeRef } = useDroppable({ id: columnId(status), disabled: !droppable });
	return (
		<div ref={setNodeRef} className="task-col-list">
			{children}
		</div>
	);
}

function ColumnBody({
	status,
	taskIds,
	tasksById,
	compact,
	columnInteractionsEnabled,
	overColumn,
	dragEnabled,
	dragSlotSize,
	onOpen,
	onMenu,
	onSwipeTaskStatus,
	taskMeta,
	tagColorByLowerName,
	assigneeColorByLowerName,
	boardDraggable,
}: {
	status: TaskBoardStatus;
	taskIds: string[];
	tasksById: Map<string, HubTask>;
	compact: boolean;
	columnInteractionsEnabled: boolean;
	overColumn: boolean;
	dragEnabled: boolean;
	boardDraggable: boolean;
	dragSlotSize?: TaskDragSlotSize;
	onOpen: (task: HubTask) => void;
	onMenu: (event: MouseEvent, task: HubTask) => void;
	onSwipeTaskStatus?: (taskId: string, direction: -1 | 1) => void;
	taskMeta: (task: HubTask) => {
		cardHint: string;
		status: TaskBoardStatus;
		tagList: string[];
		checklistDone: number;
		checklistTotal: number;
		attachmentCount: number;
		coverImageUrl: string;
		overdue: boolean;
		createdLabel: string;
		assigneeLabel: string;
		assigneeKey: string;
		dueLabel: string;
		dueLabelCompact: string;
	};
	tagColorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>;
	assigneeColorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>;
}) {
	const showEmpty = taskIds.length === 0;
	return (
		<div
			className={[
				"task-col-body",
				"hub-scroll",
				overColumn ? "is-reordering" : "",
			].filter(Boolean).join(" ")}
		>
			<ColumnTopDrop status={status} enabled={dragEnabled && !showEmpty && columnInteractionsEnabled} />
			<ColumnList status={status} droppable={!showEmpty && columnInteractionsEnabled}>
				<SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
					{showEmpty ? (
						<EmptyColumnDrop
							status={status}
							dragSlotSize={dragSlotSize}
							enabled={columnInteractionsEnabled}
						/>
					) : (
						taskIds.map((id) => {
							const task = tasksById.get(id);
							if (!task) return null;
							const meta = taskMeta(task);
							return (
								<SortableTaskCard
									key={id}
									task={task}
									compact={compact}
									boardDraggable={boardDraggable}
									columnInteractionsEnabled={columnInteractionsEnabled}
									dragSlotSize={dragSlotSize}
									enableMobileSwipe={compact && boardDraggable}
									onSwipeStatus={task.id ? (direction) => onSwipeTaskStatus?.(task.id!, direction) : undefined}
									onOpen={() => onOpen(task)}
									onMenu={(event) => onMenu(event, task)}
									{...meta}
									tagColorByLowerName={tagColorByLowerName}
									assigneeColorByLowerName={assigneeColorByLowerName}
									status={status}
								/>
							);
						})
					)}
				</SortableContext>
			</ColumnList>
		</div>
	);
}

export function TaskBoardKanban({
	tasks,
	compact,
	mobileColumn,
	columnLabel,
	onOpenTask,
	onOpenMenu,
	onMoveTask,
	onDragActivityChange,
	onMobileColumnFocus,
	onSwipeTaskStatus,
	taskMeta,
	tagColorByLowerName,
	assigneeColorByLowerName,
	boardDraggable,
}: {
	tasks: HubTask[];
	compact: boolean;
	boardDraggable: boolean;
	mobileColumn: TaskBoardStatus;
	columnLabel: Record<TaskBoardStatus, string>;
	onOpenTask: (task: HubTask) => void;
	onOpenMenu: (event: MouseEvent, task: HubTask) => void;
	onMoveTask: (taskId: string, status: TaskBoardStatus, boardPosition: number) => void;
	onDragActivityChange?: (active: boolean) => void;
	onMobileColumnFocus?: (status: TaskBoardStatus) => void;
	onSwipeTaskStatus?: (taskId: string, direction: -1 | 1) => void;
	taskMeta: (task: HubTask) => {
		cardHint: string;
		status: TaskBoardStatus;
		tagList: string[];
		checklistDone: number;
		checklistTotal: number;
		attachmentCount: number;
		coverImageUrl: string;
		overdue: boolean;
		createdLabel: string;
		assigneeLabel: string;
		assigneeKey: string;
		dueLabel: string;
		dueLabelCompact: string;
	};
	tagColorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>;
	assigneeColorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>;
}) {
	const tasksById = useMemo(
		() => new Map(tasks.map((row) => [row.id, row])),
		[tasks],
	);
	const baseline = useMemo(() => columnIdsFromTasks(tasks), [tasks]);
	const [columns, setColumns] = useState<TaskColumnIds>(baseline);
	const [activeId, setActiveId] = useState<string | null>(null);
	const [activeDragSize, setActiveDragSize] = useState<TaskDragSlotSize | undefined>(undefined);
	const dragging = useRef(false);
	const lastPlacementKey = useRef("");
	const overIdRef = useRef<string | null>(null);
	const overlayWidth = useRef(280);
	const columnsRef = useRef(baseline);
	const tasksRef = useRef(tasks);
	const dragEndHandled = useRef(false);
	const lastDropRef = useRef<{ status: TaskBoardStatus; insertIndex: number } | null>(null);
	const dropSettleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pinnedOrderKeyRef = useRef<string | null>(null);
	const mobileColumnRef = useRef(mobileColumn);
	const placementFrame = useRef<number | null>(null);
	const pendingPlacement = useRef<{ taskId: string; overRaw: string | null } | null>(null);
	const lastCommitPointerY = useRef(0);

	useEffect(() => {
		mobileColumnRef.current = mobileColumn;
	}, [mobileColumn]);

	useEffect(() => {
		tasksRef.current = tasks;
	}, [tasks]);

	useEffect(() => {
		columnsRef.current = columns;
	}, [columns]);

	useLayoutEffect(() => {
		if (typeof document === "undefined" || activeId) return;
		const sampleCard = document.querySelector<HTMLElement>(
			".task-board .task-card:not(.task-drop-placeholder):not(.task-card-column-empty)",
		);
		const emptySlot = document.querySelector<HTMLElement>('[data-task-card-slot="empty"]');
		const measureEl = sampleCard ?? emptySlot;
		if (!measureEl) return;
		const listRect = measureEl.closest(".task-col-list")?.getBoundingClientRect();
		if (listRect && listRect.width > 0) overlayWidth.current = Math.round(listRect.width);
	}, [activeId, baseline, columns, tasks.length]);

	useEffect(() => {
		if (dragging.current) return;
		const baselineKey = columnOrderKey(baseline);
		if (pinnedOrderKeyRef.current !== null && pinnedOrderKeyRef.current !== baselineKey) {
			return;
		}
		if (pinnedOrderKeyRef.current === baselineKey) {
			pinnedOrderKeyRef.current = null;
		}
		if (columnOrderKey(columnsRef.current) === baselineKey) return;
		setColumns(baseline);
		columnsRef.current = baseline;
	}, [baseline]);

	const dragOverlayModifiers = useMemo(
		() => (compact ? [snapMobileDragOverlayToPointer] : undefined),
		[compact],
	);

	// Touch first: a high PointerSensor distance blocks TouchSensor on mobile (pointerdown wins).
	const sensors = useSensors(
		useSensor(TouchSensor, {
			activationConstraint: { delay: compact ? 280 : 200, tolerance: 16 },
		}),
		useSensor(PointerSensor, {
			activationConstraint: compact ? { delay: 280, tolerance: 16 } : { distance: 6 },
		}),
	);

	const activeTask = activeId ? tasksById.get(activeId) : undefined;
	const activeMeta = activeTask ? taskMeta(activeTask) : null;
	const activeColumnStatus = activeId ? findTaskColumn(columns, activeId) : null;

	const applyDropSync = (
		current: TaskColumnIds,
		activeTaskId: string,
		drop: { status: TaskBoardStatus; insertIndex: number },
	): TaskColumnIds => {
		const next = applyColumnMove(current, activeTaskId, drop.status, drop.insertIndex);
		return next ?? current;
	};

	const handleDragStart = (event: DragStartEvent) => {
		dragging.current = true;
		if (compact) {
			taskBoardCompactDrag.active = true;
			taskBoardCompactDrag.mobileColumn = mobileColumnRef.current;
		}
		dragEndHandled.current = false;
		lastPlacementKey.current = "";
		lastDropRef.current = null;
		const coords = getEventCoordinates(event.activatorEvent);
		if (coords) {
			taskBoardPointer.x = coords.x;
			taskBoardPointer.y = coords.y;
		}
		const taskId = String(event.active.id);
		const cardEl =
			typeof document !== "undefined"
				? document.querySelector<HTMLElement>(`[data-task-id="${CSS.escape(taskId)}"]`)
				: null;
		const measured = cardEl?.getBoundingClientRect() ?? event.active.rect.current.initial;
		if (measured && measured.width > 0 && measured.height > 0) {
			const size = {
				width: Math.round(measured.width),
				height: Math.round(measured.height),
			};
			setActiveDragSize(size);
			overlayWidth.current = size.width;
		}
		onDragActivityChange?.(true);
		lastCommitPointerY.current = taskBoardPointer.y;
		setActiveId(String(event.active.id));
	};

	const overRawFromPointer = (): string | null => {
		const column = columnAtPointer(taskBoardPointer.x, taskBoardPointer.y);
		return column ? columnId(column) : null;
	};

	const revealMobileColumn = (status: TaskBoardStatus) => {
		if (!compact || status === mobileColumnRef.current) return;
		mobileColumnRef.current = status;
		if (taskBoardCompactDrag.active) {
			taskBoardCompactDrag.mobileColumn = status;
		}
		onMobileColumnFocus?.(status);
	};

	const commitPlacementSync = (activeTaskId: string, overRaw: string, preview = false) => {
		const current = columnsRef.current;
		const drop = resolveDropTarget(current, activeTaskId, overRaw, taskBoardPointer);
		if (!drop) return;
		if (
			preview
			&& previewIndexHeld(
				lastDropRef.current,
				drop,
				taskBoardPointer.y - lastCommitPointerY.current,
			)
		) {
			return;
		}
		lastDropRef.current = drop;
		if (drop.insertIndex === 0) {
			syncColumnScrollForTopDrop(drop.status, taskBoardPointer.y);
		}
		const placementKey = `${activeTaskId}|${drop.status}|${drop.insertIndex}`;
		if (placementKey === lastPlacementKey.current) {
			revealMobileColumn(drop.status);
			return;
		}
		lastPlacementKey.current = placementKey;
		lastCommitPointerY.current = taskBoardPointer.y;
		const next = applyColumnMove(current, activeTaskId, drop.status, drop.insertIndex);
		if (next) {
			columnsRef.current = next;
			setColumns(next);
		}
		// Same commit as the card move, so the source column is not hidden
		// while the active sortable node still lives in it.
		revealMobileColumn(drop.status);
	};

	const cancelScheduledPlacement = () => {
		if (placementFrame.current !== null) {
			cancelAnimationFrame(placementFrame.current);
			placementFrame.current = null;
		}
		pendingPlacement.current = null;
	};

	const schedulePlacement = (activeTaskId: string, overRaw: string | null) => {
		pendingPlacement.current = { taskId: activeTaskId, overRaw };
		if (placementFrame.current !== null) return;
		// Outside dnd-kit's layout measurement. setState from onDragOver nests
		// updates until React throws error 185 when the insert index oscillates.
		placementFrame.current = requestAnimationFrame(() => {
			placementFrame.current = null;
			const pending = pendingPlacement.current;
			pendingPlacement.current = null;
			if (!pending || !dragging.current) return;
			const tabOver = statusTabAtPointerFromBar(taskBoardPointer.x, taskBoardPointer.y);
			const effectiveOver = tabOver
				? columnId(tabOver)
				: pending.overRaw ?? overRawFromPointer();
			if (!effectiveOver) return;
			commitPlacementSync(pending.taskId, effectiveOver, true);
		});
	};

	const syncPointerFromActive = (event: DragMoveEvent | DragOverEvent) => {
		if (compact) return;
		const translated = event.active.rect.current.translated;
		if (translated) {
			taskBoardPointer.x = translated.left + translated.width / 2;
			taskBoardPointer.y = translated.top + translated.height / 2;
			return;
		}
		const initial = event.active.rect.current.initial;
		if (initial) {
			taskBoardPointer.x = initial.left + initial.width / 2;
			taskBoardPointer.y = initial.top + initial.height / 2;
		}
	};

	const clearDragUi = () => {
		cancelScheduledPlacement();
		if (dropSettleTimer.current) {
			clearTimeout(dropSettleTimer.current);
			dropSettleTimer.current = null;
		}
		setActiveId(null);
		setActiveDragSize(undefined);
		onDragActivityChange?.(false);
		dragging.current = false;
		taskBoardCompactDrag.active = false;
	};

	const handleDragOver = (event: DragOverEvent) => {
		syncPointerFromActive(event);
		const { active, over } = event;
		if (!over) {
			overIdRef.current = null;
			return;
		}
		const overRaw = String(over.id);
		overIdRef.current = overRaw;
		schedulePlacement(String(active.id), overRaw);
	};

	const handleDragMove = (event: DragMoveEvent) => {
		if (!activeId) return;
		syncPointerFromActive(event);
		schedulePlacement(activeId, overIdRef.current);
	};

	useEffect(() => {
		if (!activeId) return;
		const onPointerMove = (event: PointerEvent) => {
			taskBoardPointer.x = event.clientX;
			taskBoardPointer.y = event.clientY;
			schedulePlacement(activeId, overIdRef.current);
		};
		window.addEventListener("pointermove", onPointerMove, { passive: true });
		return () => {
			window.removeEventListener("pointermove", onPointerMove);
			cancelScheduledPlacement();
		};
	}, [activeId]);

	const handleDragEnd = (event: DragEndEvent) => {
		const taskId = String(event.active.id);
		if (dragEndHandled.current) return;
		dragEndHandled.current = true;
		cancelScheduledPlacement();
		overIdRef.current = null;
		lastPlacementKey.current = "";
		syncPointerFromActive(event);

		const overRaw = event.over ? String(event.over.id) : overRawFromPointer();
		if (overRaw) {
			commitPlacementSync(taskId, overRaw);
		}
		const drop = resolveFinalDropTarget(columnsRef.current, taskId, taskBoardPointer, overRaw)
			?? lastDropRef.current;
		const current = drop ? applyDropSync(columnsRef.current, taskId, drop) : columnsRef.current;

		columnsRef.current = current;
		setColumns(current);
		pinnedOrderKeyRef.current = columnOrderKey(current);
		lastDropRef.current = null;

		const status = findTaskColumn(current, taskId);
		if (status) {
			const move = boardMoveFromOrder(taskId, status, current[status], tasksRef.current);
			if (move) {
				onMoveTask(taskId, move.status, move.boardPosition);
				if (compact && move.status !== mobileColumn) {
					onMobileColumnFocus?.(move.status);
				}
			}
		}

		if (dropSettleTimer.current) clearTimeout(dropSettleTimer.current);
		dropSettleTimer.current = setTimeout(clearDragUi, TASK_DROP_ANIMATION_MS);
	};

	const handleDragCancel = () => {
		lastPlacementKey.current = "";
		lastDropRef.current = null;
		overIdRef.current = null;
		taskBoardCompactDrag.active = false;
		cancelScheduledPlacement();
		clearDragUi();
		setColumns(baseline);
		columnsRef.current = baseline;
	};

	useEffect(() => () => {
		if (dropSettleTimer.current) clearTimeout(dropSettleTimer.current);
	}, []);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={taskBoardCollision}
			measuring={{
				droppable: { strategy: MeasuringStrategy.WhileDragging },
			}}
			onDragStart={handleDragStart}
			onDragMove={handleDragMove}
			onDragOver={handleDragOver}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}
			autoScroll={{ threshold: { x: 0.12, y: 0.16 }, acceleration: 9, interval: 10 }}
		>
			<div className={`task-board-viewport${activeId ? " is-dragging" : ""}`}>
				<div className="board task-board">
					{TASK_BOARD_STATUSES.map((status) => {
						const ids = columns[status];
						const active = !compact || mobileColumn === status;
						return (
							<section
								className={[
									"col task-col",
									`status-${status}`,
									ids.length === 0 ? "is-empty" : "",
									active ? "is-active" : "",
								].filter(Boolean).join(" ")}
								data-task-col={status}
								key={status}
							>
								<h2>
									<span className="task-col-head">
										<span
											className={`task-col-status-mark status-${status}`}
											aria-hidden="true"
										/>
										<span className="task-col-label">{columnLabel[status]}</span>
									</span>
									<span className="task-col-count">{ids.length}</span>
								</h2>
								<ColumnBody
									status={status}
									taskIds={ids}
									tasksById={tasksById}
									compact={compact}
									columnInteractionsEnabled={active}
									boardDraggable={boardDraggable}
									overColumn={Boolean(activeId)}
									dragEnabled={boardDraggable && Boolean(activeId)}
									dragSlotSize={activeDragSize}
									onOpen={onOpenTask}
									onMenu={onOpenMenu}
									onSwipeTaskStatus={onSwipeTaskStatus}
									taskMeta={taskMeta}
									tagColorByLowerName={tagColorByLowerName}
									assigneeColorByLowerName={assigneeColorByLowerName}
								/>
							</section>
						);
					})}
				</div>
			</div>
			<DragOverlay
				adjustScale={false}
				dropAnimation={TASK_DROP_ANIMATION}
				modifiers={dragOverlayModifiers}
				className={compact ? "task-drag-overlay is-compact" : "task-drag-overlay"}
				style={
					compact
						? {
								width: "max-content",
								maxWidth: "min(260px, calc(100vw - 32px))",
								boxSizing: "border-box",
							}
						: {
								width: activeDragSize?.width || overlayWidth.current,
								maxWidth: "min(280px, calc(100vw - 24px))",
								boxSizing: "border-box",
							}
				}
			>
				{activeTask && activeMeta ? (
					compact ? (
						<TaskDragPill title={activeTask.title} />
					) : (
						<TaskBoardCard
							task={activeTask}
							dragOverlay
							status={activeColumnStatus ?? activeMeta.status}
							tagList={activeMeta.tagList}
							checklistDone={activeMeta.checklistDone}
							checklistTotal={activeMeta.checklistTotal}
							attachmentCount={activeMeta.attachmentCount}
							coverImageUrl={activeMeta.coverImageUrl}
							overdue={activeMeta.overdue}
							createdLabel={activeMeta.createdLabel}
							assigneeLabel={activeMeta.assigneeLabel}
							assigneeKey={activeMeta.assigneeKey}
							tagColorByLowerName={tagColorByLowerName}
							assigneeColorByLowerName={assigneeColorByLowerName}
							dueLabel={activeMeta.dueLabel}
							dueLabelCompact={activeMeta.dueLabelCompact}
							cardHint={activeMeta.cardHint}
							isDraggable
							sortableStyle={{ cursor: "grabbing" }}
							onOpen={() => {}}
							onMenu={() => {}}
						/>
					)
				) : null}
			</DragOverlay>
		</DndContext>
	);
}
